// src/shopConfig.ts
// -----------------------------------------------------------------------------
// The tenant config keystone for a SmartShop storefront.
//
// This is the ONE place a shop's identity lives. The storefront components
// (Layout, ShopPage, ProductPage) read everything tenant-specific from here -
// no hardcoded shop name, colour, category, or GIDO anywhere in the component
// tree. This is what turns Glen's hand-built shop (Phase 1) into a reusable
// pattern (Phase 2): same components, different config. Phase 3's onboarding
// agent will GENERATE this file per tenant.
//
// Colours are seeded here but the live source of truth is the tenant's
// SmartBrand record (smart_brand/{gido}.visual.colors); the app hydrates
// --ss-accent etc. from SmartBrand at load and falls back to these.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------

export interface OpeningHours {
  // minutes from midnight, local time; null = closed that day
  [dayOfWeek: number]: { open: number; close: number } | null;
}

export interface ShopConfig {
  gido_id: string;
  shop_name: string;
  tagline: string;              // short line under the name (e.g. location + est.)
  established?: string;         // "Est. 2019" - optional
  api_base: string;             // SmartShop cloud functions base
  territory: string;            // "IE" | "DE" | ...

  brand: {
    // seed values; SmartBrand (smart_brand/{gido}) overrides at runtime
    primary: string;            // main brand colour
    secondary: string;          // near-black / dark
    accent: string;             // the active storefront accent (--ss-accent)
    cream: string;              // light background / paper
    logo_url: string;           // path under /public
  };

  languages: string[];          // ['EN'] for now; switcher auto-hides at length 1
  locale_spelling: string;      // 'en-IE' - Irish/British English

  contact: {
    phone?: string;             // optional - Call Now hides if absent
    address_lines: string[];    // shown in footer / used for maps
    maps_destination: string;   // URL-encoded address for google maps directions
    hours: OpeningHours;        // drives the open/closed live indicator
    timezone: string;           // e.g. "Europe/Dublin"
  };

  // Navigation is DATA-DRIVEN (decision B, D-067): categories are the primary
  // nav (single product-type per product); collections are a separate strip
  // (many-to-many brand/merchandising). Both are populated from the migrated
  // catalogue, not hand-curated. Leaving these empty makes the storefront
  // derive them live from list-products; providing them pins order/labels.
  categories?: string[];        // optional explicit order; else derived live
  collections?: { name: string; logo_url?: string | null }[];  // brand strip - logo optional (placeholder until uploaded)

  // Grouped nav (presentation layer over flat categories). Phase-3 agent fills this.
  // A category absent from every group falls into "More" in the frontend.
  category_groups?: { group: string; categories: string[] }[];

  footer: {
    powered_by: string;         // "DBOS.world"
    legal_links: [string, string][];  // [label, path]
  };
}

// -----------------------------------------------------------------------------
// Red Robin Boutique - GIDO_IE_0015
// -----------------------------------------------------------------------------
export const shopConfig: ShopConfig = {
  gido_id: 'GIDO_IE_0015',
  shop_name: 'Red Robin Boutique',
  tagline: 'Ballinagh, Co. Cavan',
  established: 'Est. 2023',
  api_base: 'https://europe-west1-dbos-core-prod.cloudfunctions.net',
  territory: 'IE',

  brand: {
    // black + gold, matching her logo and SmartBrand record written today
    primary: '#D4AF37',        // gold
    secondary: '#1a1a1a',      // near-black
    accent: '#D4AF37',         // gold accent (--ss-accent); SmartBrand overrides
    cream: '#F7F3E8',          // warm paper
    logo_url: '/red-robin-logo.png',
  },

  languages: ['EN'],
  locale_spelling: 'en-IE',    // Irish/British English (colour, jumper, trousers)

  contact: {
    // phone omitted for now - Call Now button hides until provided
    address_lines: ['Main Street, Ballinagh', 'Co. Cavan, Ireland', 'H12 T9P9'],
    maps_destination: 'Main+Street,+Ballinagh,+Co.+Cavan,+Ireland+H12+T9P9',
    hours: {
      0: null,                                  // Sun closed
      1: null,                                  // Mon closed
      2: { open: 10 * 60, close: 17 * 60 },     // Tue 10-17
      3: { open: 10 * 60, close: 17 * 60 },     // Wed 10-17
      4: { open: 10 * 60, close: 18 * 60 },     // Thu 10-18
      5: { open: 10 * 60, close: 18 * 60 },     // Fri 10-18
      6: { open: 10 * 60, close: 17 * 60 },     // Sat 10-17
    },
    timezone: 'Europe/Dublin',
  },

  // categories left undefined -> derived live from list-products (her ~29 types)
  // collections = the brand strip. logo_url null -> rounded placeholder tile with
  // the name; set logo_url (upload to /public or SmartData) and the logo shows.
  // Editable by the owner; Phase-3 agent can auto-populate names + logos.
  collections: [
    { name: 'Amber Clothing', logo_url: '/brands/amber-clothing-logo.jpg' },
    { name: 'Brakeburn', logo_url: '/brands/brakeburn-logo.jpg' },
    { name: 'Girl in Mind', logo_url: '/brands/girl-in-mind-logo.jpg' },
    { name: 'Hanna B Designs', logo_url: '/brands/hanna-b-designs-logo.jpg' },
    { name: 'Hope & Ivy', logo_url: '/brands/hope-and-ivy.jpg' },
    { name: 'ICHI', logo_url: '/brands/ichi-logo.jpg' },
    { name: 'Kaffe', logo_url: '/brands/kaffe-logo.jpg' },
    { name: 'Kate & Pippa', logo_url: '/brands/kate-and-pippa-logo.jpg' },
    { name: 'Marc Angelo', logo_url: '/brands/marc-angelo-logo.jpg' },
    { name: 'New Collection', logo_url: '/brands/new-collection-logo.jpg' },
    { name: 'Rant & Rave', logo_url: '/brands/rant-and-rave-irish-brand-logo.jpg' },
    { name: 'So Amazing', logo_url: '/brands/so-amazing-logo.jpg' },
    { name: 'Style Direct', logo_url: '/brands/style-direct-logo.jpg' },
    { name: 'Susst', logo_url: '/brands/susst-logo.jpg' },
  ],

  // ---- Category groups (the grouped nav, matching her reference shop) --------
  // Maps the flat product categories (from migration) UP into a handful of parent
  // groups for the header/menu. This is a PRESENTATION layer - the product's
  // `category` field stays flat; grouping is merchandising, editable, not baked
  // into the data. A category not listed in any group falls into "More" (the
  // frontend handles the remainder gracefully), so a shop with NO groups still
  // gets a working flat nav.
  //
  // PHASE 3: the onboarding agent GENERATES this exact structure automatically
  // from the tenant's distinct categories (grouping is an LLM judgment task).
  // Hand-authored here for Phil; the shape is the automation contract.
  category_groups: [
    { group: 'New In',      categories: [] },                                  // filled when New Arrivals collection exists
    { group: 'Dresses',     categories: ['Dresses', 'One-Pieces'] },
    { group: 'Tops',        categories: ['Blouses', 'Shirts', 'T-Shirts', 'Tank Tops', 'Sweaters', 'Sweatshirts', 'Cardigans', 'Hoodies'] },
    { group: 'Bottoms',     categories: ['Jeans', 'Trousers', 'Chinos', 'Leggings', 'Joggers', 'Skirts', 'Bermudas'] },
    { group: 'Jackets & Coats', categories: ['Bomber Jackets', 'Puffer Jackets', 'Sport Jackets', 'Trucker Jackets', 'Coats & Jackets', 'Overcoats', 'Pea Coats', 'Vests'] },
    { group: 'Accessories', categories: ['Clutch Bags', 'Earrings'] },
    { group: 'Gift Cards',  categories: ['Gift Cards'] },
  ],

  footer: {
    powered_by: 'DBOS.world',
    legal_links: [
      ['Privacy Policy', '/privacy'],
      ['Cookies', '/cookies'],
      ['Terms', '/terms'],
      ['Shipping & Returns', '/shipping'],
      ['Contact', '/contact'],
    ],
  },
};
